/**
 * TRAINING (EXPERT-ROOMS.md §15d, owner 2026-10-26) — ONE view of what an expert knows and what changed: what Training
 * (dev) knows now per team (rules · trained answers · procedures), what waits for a person, and COMPARE any two of
 * Training (now) / QA / Production / a release → added · changed · removed. Read-only; it lives beside the Rules.
 * Backend: PUT /networks/{id} ``{expertReleases: {action: 'training'}}`` · ``{action: 'diff', from, to}``.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { getRestApiBearerToken } from '@/services/apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const KIND = { rule: 'Rule', answer: 'Answer', procedure: 'Procedure', fact: 'Fact kind' };

async function call(networkId, req) {
  const r = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(networkId)}`, {
    method: 'PUT', headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ expertReleases: req }),
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.message || `HTTP ${r.status}`);
  return body.expertReleases || {};
}

/** The compare choices: Training (now) · each running stage · every release (newest first). Pure. */
export function compareOptions(xr) {
  const opts = [{ value: 'live', label: 'Training (now)' }];
  for (const s of (xr?.stages || [])) {
    if (s.stage !== 'dev' && s.version) opts.push({ value: s.stage, label: `${s.stage === 'prod' ? 'Production' : 'QA'} (${s.label || `v${s.version}`})` });
  }
  for (const r of (xr?.releases || [])) opts.push({ value: String(r.version), label: `Release ${r.label || `v${r.version}`}` });
  return opts;
}

function Item({ it, mark }) {
  const tone = { '+': 'text-emerald-700 bg-emerald-50', '~': 'text-amber-800 bg-amber-50', '−': 'text-rose-700 bg-rose-50' }[mark];
  return (
    <li className="flex gap-2 py-1.5" data-testid="training-diff-item">
      <span className={`mt-px h-4 w-4 shrink-0 rounded text-center text-[11px] font-bold leading-4 ${tone}`}>{mark}</span>
      <div className="min-w-0 text-xs">
        <div className="text-[11px] text-gray-500">{KIND[it.kind] || it.kind}{it.team ? ` · ${it.team}` : ''}</div>
        <div className="font-medium text-gray-800">{it.title}</div>
        {mark === '~' ? (<><div className="text-rose-700 line-through">{it.before}</div><div className="text-emerald-700">{it.body}</div></>)
          : it.kind !== 'procedure' ? <div className={mark === '−' ? 'text-gray-500 line-through' : 'text-gray-600'}>{it.body}</div> : null}
      </div>
    </li>
  );
}

function Team({ t, open, onToggle }) {
  return (
    <div className="border-b border-gray-100" data-testid={`training-team-${t.name}`}>
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-1.5 py-2 text-left text-xs">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
        <span className="font-medium text-gray-800">{t.name}</span>
        <span className="text-gray-500">{t.rules.length} rules · {t.answers.length} trained answers</span>
      </button>
      {open ? (
        <div className="pb-2 pl-5 text-xs">
          {t.rules.map((r, i) => <div key={`r${i}`} className="py-0.5 text-gray-700">{r.body}</div>)}
          {t.answers.length ? <div className="mt-1.5 text-[11px] font-medium text-gray-500">Trained answers</div> : null}
          {t.answers.map((a, i) => (
            <div key={`a${i}`} className="py-0.5"><span className="text-gray-800">{a.title}</span>
              <span className="text-gray-500"> — {a.body}</span></div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function ExpertTrainingView({ network }) {
  const networkId = network?.networkId;
  const xr = network?.expertReleases;
  const options = useMemo(() => compareOptions(xr), [xr]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [openTeam, setOpenTeam] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('live');
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exp, setExp] = useState(null);        // {ref, links, counts} — the JSONL export of the chosen version
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    if (!networkId) return;
    setErr('');
    call(networkId, { action: 'training' }).then((r) => setData(r.training || null)).catch((e) => setErr(e.message));
  }, [networkId]);
  useEffect(() => { if (!from) setFrom(options.find((o) => o.value === 'prod')?.value || options[1]?.value || ''); }, [options, from]);
  const compare = useCallback(async () => {
    setBusy(true); setErr('');
    try { setDiff((await call(networkId, { action: 'diff', from, to })).diff || null); } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, [networkId, from, to]);
  const exportJsonl = async () => {
    setExporting(true); setErr('');
    try { setExp((await call(networkId, { action: 'export', ref: to })).export || null); } catch (e) { setErr(e.message); }
    finally { setExporting(false); }
  };
  if (!data) return <div className="p-4 text-xs text-gray-500" data-testid="training-loading">{err || 'Loading…'}</div>;
  const c = data.counts || {};
  const w = data.waiting || {};
  return (
    <div className="p-4 text-[12px]" data-testid="expert-training-view">
      <p className="text-xs text-gray-600" data-testid="training-counts">
        Training knows <b>{c.rules || 0}</b> rules, <b>{c.answers || 0}</b> trained answers and <b>{c.procedures || 0}</b> procedures
        across {c.teams || 0} team{c.teams === 1 ? '' : 's'}.
        {w.questions || w.ruleProposals ? (
          <span className="ml-1 text-amber-800" data-testid="training-waiting">
            Waiting for a person: {w.questions ? `${w.questions} question${w.questions === 1 ? '' : 's'} to confirm${w.fromProduction ? ` (${w.fromProduction} from Production)` : ''}` : ''}
            {w.questions && w.ruleProposals ? ' · ' : ''}{w.ruleProposals ? `${w.ruleProposals} rule proposal${w.ruleProposals === 1 ? '' : 's'}` : ''}.
          </span>
        ) : null}
      </p>

      {options.length > 1 ? (
        <div className="mt-3 rounded-md border border-gray-200 p-2" data-testid="training-compare">
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-gray-600">Compare</span>
            <select value={from} onChange={(e) => { setFrom(e.target.value); setDiff(null); }} data-testid="training-from"
              className="rounded border border-gray-300 px-1 py-0.5 text-xs">
              {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span className="text-gray-400">→</span>
            <select value={to} onChange={(e) => { setTo(e.target.value); setDiff(null); }} data-testid="training-to"
              className="rounded border border-gray-300 px-1 py-0.5 text-xs">
              {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button type="button" disabled={busy || from === to} onClick={compare} data-testid="training-compare-button"
              className={`${btn} inline-flex items-center gap-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}Compare
            </button>
            <button type="button" disabled={exporting} onClick={exportJsonl} data-testid="training-export-button"
              title="Download the right-hand version as JSONL — its Q&A as conversations + its rules and procedures"
              className={`${btn} inline-flex items-center gap-1 border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}>
              {exporting ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}Export JSONL
            </button>
          </div>
          {exp ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]" data-testid="training-export">
              <span className="text-gray-500">{exp.counts?.qa ?? 0} Q&amp;A · {exp.counts?.rules ?? 0} rules &amp; procedures —</span>
              {Object.entries(exp.links || {}).map(([name, href]) => (
                <a key={name} href={href} className="font-medium text-indigo-700 hover:underline" data-testid={`training-export-${name}`}>{name}</a>
              ))}
              <span className="text-gray-400">(links last an hour)</span>
            </div>
          ) : null}
          {diff ? (
            <div className="mt-2" data-testid="training-diff">
              <p className="text-[11px] text-gray-500">
                {diff.counts.added} added · {diff.counts.changed} changed · {diff.counts.removed} removed
              </p>
              {diff.counts.added + diff.counts.changed + diff.counts.removed === 0
                ? <p className="mt-1 text-xs text-gray-500">No differences.</p> : (
                  <ul className="mt-1 divide-y divide-gray-50">
                    {diff.added.map((it) => <Item key={`+${it.key}`} it={it} mark="+" />)}
                    {diff.changed.map((it) => <Item key={`~${it.key}`} it={it} mark="~" />)}
                    {diff.removed.map((it) => <Item key={`-${it.key}`} it={it} mark="−" />)}
                  </ul>)}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3" data-testid="training-teams">
        {(data.teams || []).map((t) => (
          <Team key={t.name} t={t} open={openTeam === t.name} onToggle={() => setOpenTeam(openTeam === t.name ? '' : t.name)} />
        ))}
        {(data.procedures || []).length ? (
          <div className="py-2 text-xs" data-testid="training-procedures">
            <span className="font-medium text-gray-800">Procedures</span>
            <span className="text-gray-500"> — {data.procedures.map((p) => p.title).join(' · ')}</span>
          </div>
        ) : null}
      </div>
      {err ? <p className="mt-2 text-[11px] text-rose-600">{err}</p> : null}
    </div>
  );
}
